import { useState, useEffect } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Zap, Store, Smartphone, LayoutDashboard, Film, Tv, Newspaper, Clock, Mic2, Users, CreditCard, BarChart3, Settings, Menu, X, LogOut, ArrowLeft, Loader2, ShieldAlert, Vote, Activity, Ticket, ScanLine, Home, UserSquare2, ShieldCheck, ClipboardList, History, MessageSquare, MessageSquareText, Star, Trophy, DollarSign, Bell, Megaphone, Plug, RotateCcw, Split, Percent } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { endpoints } from "@/lib/api";

// [path, label, Icon, gate] — gate("main") main-admin-only; gate("mod","action"); gate("area") any admin user.
const NAV = [
  ["", "Dashboard", LayoutDashboard, ["main"]],
  ["approvals", "Approvals", ClipboardList, ["approvals"]],
  ["homepage", "Homepage Content", Home, ["main"]],
  ["journalists", "Our Journalists", UserSquare2, ["mod", "journalists", "view"]],
  ["shows", "Shows / VOD", Film, ["mod", "shows", "view"]],
  ["live", "Live Control", Tv, ["main"]],
  ["voting", "Voting", Vote, ["mod", "voting", "view"]],
  ["events", "Events & Tickets", Ticket, ["mod", "events", "view"]],
  ["creators", "Creators", Star, ["main"]],
  ["vod", "Videos (VOD)", Film, ["main"]],
  ["football", "Football", Trophy, ["main"]],
  ["scanner", "Entrance Scanner", ScanLine, ["mod", "scanner", "checkin"]],
  ["engagement", "Engagement", Activity, ["main"]],
  ["news", "News", Newspaper, ["mod", "news", "view"]],
  ["schedule", "Schedule", Clock, ["mod", "schedule", "view"]],
  ["programs", "Programs", Mic2, ["mod", "programs", "view"]],
  ["users", "Customers", Users, ["main"]],
  ["support", "Support Inbox", MessageSquare, ["area"]],
  ["staff", "Staff & Access", ShieldCheck, ["main"]],
  ["activity", "Activity Log", History, ["main"]],
  ["payments", "Payments", CreditCard, ["main"]],
  ["api-management", "API Management", Plug, ["main"]],
  ["whatsapp", "WhatsApp (Meta)", MessageSquareText, ["main"]],
  ["revenue-split", "Revenue Split 50/50", Split, ["main"]],
  ["visitors", "Visitors & Push", BarChart3, ["main"]],
  ["store-assets", "App Store Screenshots", Smartphone, ["main"]],
  ["utilities", "Utilities (Efashe)", Zap, ["main"]],
  ["merchants", "Merchants", Store, ["mod", "merchants", "view"]],
  ["commissions", "Utility Commissions", Percent, ["mod", "commissions", "view"]],
  ["recovery", "Recovery (Super Admin)", RotateCcw, ["super"]],
  ["reports", "Reports", BarChart3, ["main"]],
  ["settings", "Settings", Settings, ["main"]],
  ["pricing", "Pricing & Currencies", DollarSign, ["main"]],
  ["messages", "Automated Messages", MessageSquareText, ["main"]],
  ["notifications", "Notifications", Bell, ["main"]],
  ["ads", "Advertisements", Megaphone, ["main"]],
];

const WORKFLOW_MODS = ["news", "shows", "events", "voting"];

export default function AdminLayout() {
  const { ready, isAuthed, isAdminArea, isMainAdmin, can, logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const nav = useNavigate();

  const canApprovals = isMainAdmin || WORKFLOW_MODS.some((m) => can(m, "view") || can(m, "verify") || can(m, "approve"));

  useEffect(() => {
    if (!isAdminArea) return;
    let alive = true;
    const load = () => endpoints.adminWorkflowPending().then((r) => { if (alive) setPending(r.data?.total || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdminArea]);

  if (!ready) return <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;

  const broadcastOnly = user?.role === "staff" && Object.entries(user.permissions || {}).every(([k, v]) => k === "broadcast" || !(v || []).length)
    && (user.permissions?.broadcast || []).length > 0;
  if (broadcastOnly) return <Navigate to="/broadcast" replace />;

  if (!isAuthed || !isAdminArea) {
    return (
      <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <ShieldAlert className="w-14 h-14 text-[#E11D2A] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Admin access required</h1>
          <p className="text-slate-400 text-sm mt-2">Sign in with an administrator or staff account to manage B&B KIGALI.</p>
          <div className="flex gap-3 justify-center mt-6">
            {!isAuthed && <button onClick={() => window.dispatchEvent(new Event("open-auth"))} data-testid="admin-login-btn" className="h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold">Sign in</button>}
            <Link to="/" className="h-11 px-6 rounded-full bg-white/5 border border-[#273042] text-white font-semibold flex items-center">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const visible = NAV.filter(([, , , gate]) => {
    if (gate[0] === "area") return true;
    if (gate[0] === "main") return isMainAdmin;
    if (gate[0] === "super") return isMainAdmin && !!user?.isSuperAdmin;
    if (gate[0] === "approvals") return canApprovals;
    if (gate[0] === "mod") return can(gate[1], gate[2]);
    return false;
  });

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 h-16 border-b border-[#273042]">
        <img src="/bb-logo.png" alt="B&B KIGALI" className="h-9 w-auto" />
        <span className="text-[10px] text-[#E11D2A] font-bold uppercase tracking-wider">{user?.isSuperAdmin ? "Super Admin" : isMainAdmin ? "Admin" : "Staff"}</span>
      </Link>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visible.map(([path, label, Icon]) => (
          <NavLink key={path} to={path === "" ? "/admin" : `/admin/${path}`} end={path === ""} onClick={() => setOpen(false)}
            data-testid={`admin-nav-${path || "dashboard"}`}
            className={({ isActive }) => `flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#E11D2A] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
            <span className="flex items-center gap-3"><Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {label}</span>
            {path === "approvals" && pending > 0 && <span data-testid="pending-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-[#E11D2A] text-white text-[11px] font-bold flex items-center justify-center">{pending}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#273042] space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
        <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-red-400 hover:bg-white/5"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0C10] flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#121620] border-r border-[#273042] fixed inset-y-0"><Sidebar /></aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#121620] border-r border-[#273042]"><Sidebar /></aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        <div className="lg:hidden sticky top-0 z-40 glass border-b border-[#273042] h-14 flex items-center px-4 gap-3">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn" className="text-white"><Menu className="w-6 h-6" /></button>
          <img src="/bb-logo.png" alt="BB" className="h-7 w-auto" />
          <span className="text-xs text-[#E11D2A] font-bold">{user?.isSuperAdmin ? "SUPER ADMIN" : isMainAdmin ? "ADMIN" : "STAFF"}</span>
        </div>
        <div className="p-5 sm:p-8 max-w-6xl mx-auto"><Outlet /></div>
      </div>
    </div>
  );
}
